/**
 * useBootstrap(wsId) — Feb 2026 · R293
 *
 * Prefetches the server fan-out at `/workspaces/{ws}/bootstrap` and
 * seeds the module-scope caches of the consumer hooks so later renders
 * find data already hot. Returns the latest bootstrap payload for any
 * caller that wants to read it directly.
 *
 * One shared fetch per workspace per `MIN_INTERVAL`. All subscribers
 * share the same inflight Promise, so N components mounting at once
 * trigger one network round-trip.
 *
 * The hook is additive — individual consumers (useWorkspacePerms,
 * compliance badge polling, etc) still work on their own if this hook
 * never fires. Bootstrap simply skips the first redundant call per
 * page load, which is where the "6 calls per page" cost lived.
 */
import { useEffect, useRef, useState } from "react";
import api from "@/lib/api";
import { primeFromBootstrap as _primePerms } from "@/lib/useWorkspacePerms";

const _cache = new Map();         // wsId → { at: number, data: object }
const _inflight = new Map();      // wsId → Promise
const _subscribers = new Map();   // wsId → Set<listener>
const MIN_INTERVAL_MS = 30_000;   // Don't re-call bootstrap more often than this.

function _notify(wsId) {
  const subs = _subscribers.get(wsId);
  if (!subs) return;
  const snap = _cache.get(wsId)?.data || null;
  subs.forEach((fn) => { try { fn(snap); } catch { /* subscriber threw */ } });
}

export function fetchBootstrap(wsId, { force = false } = {}) {
  if (!wsId) return Promise.resolve(null);
  const cached = _cache.get(wsId);
  const fresh = cached && (Date.now() - cached.at) < MIN_INTERVAL_MS;
  if (fresh && !force) return Promise.resolve(cached.data);
  if (_inflight.has(wsId)) return _inflight.get(wsId);

  const p = api.get(`/workspaces/${wsId}/bootstrap`)
    .then((r) => {
      const data = r.data || {};
      _cache.set(wsId, { at: Date.now(), data });
      // Seed the per-hook caches so consumers skip their own first fetch.
      const perms = data.permissions;
      if (perms && !perms.error) _primePerms(wsId, perms);
      _notify(wsId);
      return data;
    })
    .catch((e) => {
      // Fail-open: let the individual consumers fall back to their own
      // endpoints. We still clear inflight so a retry can happen later.
      // eslint-disable-next-line no-console
      console.warn("bootstrap fetch failed", e?.message || e);
      _notify(wsId);
      return _cache.get(wsId)?.data || null;
    })
    .finally(() => { _inflight.delete(wsId); });
  _inflight.set(wsId, p);
  return p;
}

export function clearBootstrapCache(wsId) {
  if (wsId) _cache.delete(wsId); else _cache.clear();
}

// Feb 2026 · R293 · Exposed so `useWorkspacePerms._fetch` can dedupe
// its first-page-load call against an in-flight bootstrap. Returns the
// Promise if one is in-flight, otherwise null — the caller decides
// whether to await it or fall back to the standalone endpoint.
export function getInflightBootstrap(wsId) {
  return _inflight.get(wsId) || null;
}

export default function useBootstrap(wsId) {
  const [data, setData] = useState(() => _cache.get(wsId)?.data || null);
  // Feb 2026 · R293 — Fire the bootstrap fetch DURING render (idempotent,
  // guarded by a ref so it only fires once per wsId per instance). Needed
  // because React runs child-component effects BEFORE parent effects, and
  // `useWorkspacePerms` is called from NavGroup children — if we only
  // kicked the fetch inside `useEffect`, every NavGroup would already
  // have hit `/me/permissions/{ws}` by the time the parent's effect ran.
  // Firing in render is safe here because `fetchBootstrap` is a plain
  // read + Promise registration with no React state writes.
  const firedForRef = useRef(null);
  if (wsId && firedForRef.current !== wsId) {
    firedForRef.current = wsId;
    fetchBootstrap(wsId);
  }

  useEffect(() => {
    if (!wsId) { setData(null); return; }
    // Sync from cache immediately for mount-time flash prevention.
    const cached = _cache.get(wsId)?.data || null;
    if (cached) setData(cached);

    // Subscribe.
    let subs = _subscribers.get(wsId);
    if (!subs) { subs = new Set(); _subscribers.set(wsId, subs); }
    const listener = (snap) => setData(snap);
    subs.add(listener);

    // Fire a fetch (dedup inside).
    fetchBootstrap(wsId);

    return () => {
      const s = _subscribers.get(wsId);
      if (!s) return;
      s.delete(listener);
      if (s.size === 0) _subscribers.delete(wsId);
    };
  }, [wsId]);

  return data;
}
